import type { Logger } from '#src/assets/lib/logger.ts'

function swapScriptType({
  script,
  nextType,
}: {
  script: HTMLScriptElement
  nextType: 'text/javascript' | 'text/plain'
}): void {
  const newScript = document.createElement('script')

  Array.from(script.attributes).forEach(attr => {
    newScript.setAttribute(attr.name, attr.value)
  })

  newScript.setAttribute('type', nextType)

  if (!script.hasAttribute('src')) {
    newScript.text = script.text
  }

  script.replaceWith(newScript)
}

function pushCategoryEventToDataLayer({
  category,
  isConsented,
  log,
}: {
  category: string
  isConsented: boolean
  log: Logger['log']
}): void {
  const eventName = `lcc.cookie.${category}.${isConsented ? 'accepted' : 'rejected'}`

  if (!window.dataLayer) {
    return
  }

  const alreadyPushed = window.dataLayer.some(
    item =>
      typeof item === 'object' &&
      item !== null &&
      'event' in item &&
      item.event === eventName,
  )

  if (!alreadyPushed) {
    window.dataLayer.push({ event: eventName })
    log(`Pushed dataLayer event: ${eventName}`)
  }
}

export function processConsentedScripts(
  consents: Record<string, boolean>,
  log: Logger['log'],
): void {
  log('Processing consented scripts:', consents)

  Object.keys(consents).forEach(category => {
    if (category === 'necessary') {
      return
    }

    const isConsented = consents[category] ?? false
    const selector = `[data-cookieconsent="${category}"]`
    const scripts = document.querySelectorAll<HTMLScriptElement>(selector)

    log(`Found ${scripts.length} scripts for category: ${category}`)

    scripts.forEach(script => {
      const currentType = script.getAttribute('type')

      if (isConsented && currentType === 'text/plain') {
        swapScriptType({ script, nextType: 'text/javascript' })
        log(`Activated script for category: ${category}`)
      } else if (!isConsented && currentType === 'text/javascript') {
        swapScriptType({ script, nextType: 'text/plain' })
        log(`Blocked script for category: ${category}`)
      }
    })
  })

  Object.keys(consents).forEach(category => {
    if (category === 'necessary') {
      return
    }

    pushCategoryEventToDataLayer({
      category,
      isConsented: consents[category] ?? false,
      log,
    })
  })
}
